import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useLingui } from "@lingui/react/macro";
import { Trans } from "@lingui/react";
import { AlertTriangle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { BaseEditableForm } from "@/components/form/BaseEditableForm";
import type { FieldConfig, GroupConfig } from "@/components/form/types";
import {
  useSignOffSettlingIn,
  useUndoSignOffSettlingIn,
  useCompletionWarnings,
  useSettlingInBillingPreview,
} from "@/hooks/useSettlingIn";
import { useChildRelationships } from "@/hooks/useChildContactRelationships";
import { BillingPreviewBlock } from "@/components/settling-in/BillingPreviewBlock";
import { settlingin_CompletePlanWarningDTO } from "@/types/settlingIn";
import { toOrgDateOnly } from "@/utils/dateUtils";
import { format } from "date-fns";
import { typeError } from "@/lib/zodTypeError";

interface SignOffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planId: string;
  childId: string;
  isSignedOff?: boolean;
  signOffDate?: string;
  signOffContactId?: string;
}

interface SignOffFormData extends Record<string, unknown> {
  sign_off_date: string;
  sign_off_contact_id: string;
  acknowledged?: boolean;
}

/**
 * What the CONTROLS hold, before the schema's transform runs: the date field accepts
 * either a picker `Date` or an already-formatted string. `BaseEditableForm`'s second
 * generic is exactly this input side (`z.ZodType<Output, _, Input>`).
 */
interface SignOffFormInput extends Record<string, unknown> {
  sign_off_date: string | Date;
  sign_off_contact_id: string;
  acknowledged?: boolean;
}

/** Debounce window for re-fetching the preview as the sign-off date is edited. */
const AS_OF_DEBOUNCE_MS = 400;

/** Normalise a form date value (string or Date) to "yyyy-MM-dd", or undefined. */
function toAsOf(value: unknown): string | undefined {
  if (value instanceof Date) return format(value, "yyyy-MM-dd");
  if (typeof value === "string" && value.length > 0) return value.slice(0, 10);
  return undefined;
}

export function SignOffDialog({ open, onOpenChange, planId, childId, isSignedOff, signOffDate, signOffContactId }: SignOffDialogProps) {
  const { t } = useLingui();
  const signOffMutation = useSignOffSettlingIn();
  const undoSignOffMutation = useUndoSignOffSettlingIn();

  const { data: relationships } = useChildRelationships(childId, !!childId);

  /**
   * Both branches go through `toOrgDateOnly`: a stored "yyyy-MM-dd" passes
   * through untouched instead of being round-tripped via `new Date()` (which
   * parses it as UTC midnight and shifts the day west of UTC), and "today" is
   * resolved in the org timezone rather than the browser's.
   */
  const initialAsOf = useMemo(
    () =>
      isSignedOff && signOffDate
        ? toOrgDateOnly(signOffDate)
        : toOrgDateOnly(new Date().toISOString()),
    [isSignedOff, signOffDate],
  );

  /**
   * The sign-off date the operator is CURRENTLY entering (adversarial resolution
   * A1). On a not-yet-signed-off plan `sign_off_date` is null server-side, so a
   * period derived from it alone would always be empty — for the exact action
   * this preview gates. Passing the unsaved form value as `as_of` makes the shown
   * period track the date being recorded. It is read-only input to the period
   * formatting and is never persisted.
   */
  const [asOf, setAsOf] = useState<string | undefined>(initialAsOf);
  const [debouncedAsOf, setDebouncedAsOf] = useState<string | undefined>(initialAsOf);

  // Re-seed when the dialog reopens against a different plan/date.
  useEffect(() => {
    if (open) {
      setAsOf(initialAsOf);
      setDebouncedAsOf(initialAsOf);
    }
  }, [open, initialAsOf]);

  // Debounce so typing a date does not fire one request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAsOf(asOf), AS_OF_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [asOf]);

  const handleFieldChange = useCallback((name: string, value: unknown) => {
    if (name === "sign_off_date") setAsOf(toAsOf(value));
  }, []);

  /**
   * Sign-off can be the PAIR-COMPLETING action — billing is automatic once
   * completion and sign-off both exist — so it needs the same preview and the
   * same soft gate `CompleteDialog` has. Neither existed here before ST-6/S18.
   */
  const previewQuery = useSettlingInBillingPreview(planId, open && !isSignedOff, debouncedAsOf);

  const warningsQuery = useCompletionWarnings(planId, open && !isSignedOff);
  /**
   * `GetCompletionWarnings` emits `sign_off_missing` for as long as the plan is
   * unsigned — which is precisely the state THIS dialog's action resolves. Left
   * in, every sign-off would force the operator to acknowledge the very thing
   * they are about to do. Filtered here only; other warning types still gate.
   */
  const warnings = useMemo(
    () =>
      (warningsQuery.data?.warnings ?? []).filter(
        (w) => w.type !== settlingin_CompletePlanWarningDTO.type.SIGN_OFF_MISSING,
      ),
    [warningsQuery.data],
  );
  /**
   * A FAILED warnings fetch is not an empty warning set: `data` is `undefined` in
   * both cases and `isLoading` is false on the error path too. Left unhandled, a
   * 500 here would silently drop the acknowledgment.
   */
  const warningsFailed = !isSignedOff && warningsQuery.isError;

  const needsAck = !isSignedOff && warnings.length > 0;

  const contactOptions = useMemo(() => {
    if (!relationships || !Array.isArray(relationships)) return [];
    return relationships
      .filter((r: { contactId?: string; contactFirstname?: string; contactLastname?: string }) => r.contactId)
      .map((r: { contactId?: string; contactFirstname?: string; contactLastname?: string }) => ({
        value: r.contactId || "",
        label: `${r.contactFirstname || ""} ${r.contactLastname || ""}`.trim(),
      }));
  }, [relationships]);

  const dateRequiredMsg = t({ id: "settlingIn.signOff.validation.dateRequired", message: "Date is required" });
  const ackRequiredMsg = t({
    id: "settlingIn.signOff.validation.acknowledgeRequired",
    message: "Please confirm you want to record this despite the warnings",
  });

  const schema = useMemo(() => z.object({
    sign_off_date: z.union([
      z.string().min(1, dateRequiredMsg),
      z.date({ error: typeError({ required: dateRequiredMsg }) }),
    ]).transform((val) => {
      if (val instanceof Date) return format(val, "yyyy-MM-dd");
      return val;
    }),
    sign_off_contact_id: z.string().min(1, t({ id: "settlingIn.signOff.validation.contactRequired", message: "Contact is required" })),
    // Declared unconditionally (relaxed to an optional boolean when the
    // acknowledgement is not required) rather than conditionally spread: an optional
    // KEY in the shape types the parsed value as `unknown`, which no form field type
    // can accept. The field is only rendered — and only validated as `literal(true)`
    // — when `needsAck`, so nothing changes for the user.
    acknowledged: needsAck
      ? z.literal(true, { error: () => ackRequiredMsg })
      : z.boolean().optional(),
  }), [t, dateRequiredMsg, needsAck, ackRequiredMsg]);

  const fields = useMemo<FieldConfig<SignOffFormData>[]>(() => [
    {
      name: "sign_off_date",
      label: t({ id: "settlingIn.signOff.date", message: "Sign-Off Date" }),
      type: "date",
      required: true,
    },
    {
      name: "sign_off_contact_id",
      label: t({ id: "settlingIn.signOff.contact", message: "Contact" }),
      type: "select",
      required: true,
      selectOptions: contactOptions,
      placeholder: t({ id: "settlingIn.signOff.selectContact", message: "Select contact" }),
    },
    ...(needsAck
      ? [{
          name: "acknowledged" as const,
          label: t({
            id: "settlingIn.signOff.recordAnyway",
            message: "Record anyway despite the warnings above",
          }),
          type: "boolean" as const,
          required: true,
        }]
      : []),
  ], [t, contactOptions, needsAck]);

  const groups = useMemo<GroupConfig<SignOffFormData>[]>(() => [
    {
      title: t({ id: "settlingIn.signOff.groupTitle", message: "Sign-Off Details" }),
      fields: needsAck
        ? ["sign_off_date", "sign_off_contact_id", "acknowledged"]
        : ["sign_off_date", "sign_off_contact_id"],
    },
  ], [t, needsAck]);

  const defaultValues = useMemo<Partial<SignOffFormData>>(() => ({
    sign_off_date: initialAsOf,
    sign_off_contact_id: isSignedOff && signOffContactId ? signOffContactId : "",
    acknowledged: false,
  }), [initialAsOf, isSignedOff, signOffContactId]);

  /**
   * The preview is the figure the operator is consenting to, so sign-off may
   * only proceed while it actually describes the date being submitted:
   *
   * - `debouncedAsOf === asOf` — inside the 400ms debounce the query still holds
   *   the PREVIOUS date's period, so submitting then would record a date the
   *   operator never saw priced.
   * - `status === "success"` — pending and errored previews render no figure at
   *   all. A disabled query also sits at `pending`, which is why this is gated
   *   on `success` rather than `!isLoading && !isError`.
   *
   * The edit path never fetches a preview, so it bypasses the gate entirely.
   */
  const previewMatchesForm =
    isSignedOff || (debouncedAsOf === asOf && previewQuery.status === "success");

  const handleSubmit = async (values: SignOffFormData) => {
    // Guard the handler too, not just the disabled prop: Enter-to-submit and any
    // programmatic submit bypass the button's disabled state.
    if (!previewMatchesForm) return;
    await signOffMutation.mutateAsync({
      id: planId,
      data: {
        sign_off_date: values.sign_off_date,
        sign_off_contact_id: values.sign_off_contact_id,
      },
    });
    onOpenChange(false);
  };

  const handleDelete = async () => {
    await undoSignOffMutation.mutateAsync(planId);
    onOpenChange(false);
  };

  /**
   * Close the warnings-in-flight bypass: the query starts pending, so `warnings`
   * is `[]` on first paint and a fast submit would skip the acknowledgment.
   * Gate on `status !== "success"` rather than `isLoading || isError` — a DISABLED
   * query sits at status `pending` with fetchStatus `idle`, so `isLoading` is
   * false while the warning set is still unknown. `success` is the only state in
   * which it is actually known. The edit path never fetches warnings.
   */
  const isSubmitting =
    signOffMutation.isPending ||
    (!isSignedOff && warningsQuery.status !== "success") ||
    !previewMatchesForm;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isSignedOff
              ? <Trans id="settlingIn.signOff.editTitle">Edit Sign-Off</Trans>
              : <Trans id="settlingIn.signOff.title">Sign Off Settling-In</Trans>
            }
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans id="common.dialog.formDescription" message="Fill in the fields, then save." />
          </DialogDescription>
        </DialogHeader>

        {!isSignedOff && (
          <BillingPreviewBlock
            data={previewQuery.data}
            isLoading={previewQuery.isLoading}
            isError={previewQuery.isError}
            isFetching={previewQuery.isFetching}
            onRetry={() => void previewQuery.refetch()}
          />
        )}

        {warningsFailed && (
          <div
            className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
            role="alert"
            data-testid="signoff-warnings-error"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="space-y-2">
              <span>
                <Trans id="settlingIn.signOff.warning.loadFailed">
                  The completion warnings could not be loaded, so this plan cannot be signed off yet. Retry to continue.
                </Trans>
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void warningsQuery.refetch()}
                disabled={warningsQuery.isFetching}
              >
                <Trans id="common.retry">Try again</Trans>
              </Button>
            </div>
          </div>
        )}

        {!isSignedOff && warnings.length > 0 && (
          <div className="space-y-2 py-2" data-testid="signoff-warnings">
            {warnings.map((warning) => (
              <div key={warning.type} className="flex items-start gap-2 rounded-md border border-warning-on-surface/40 bg-warning-muted p-3 text-sm text-warning-on-surface">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>
                  {/* `sign_off_missing` is filtered out above — this dialog resolves it. */}
                  {warning.type === settlingin_CompletePlanWarningDTO.type.FUTURE_SCHEDULE_ENTRIES && (
                    <Trans
                      id="settlingIn.complete.warning.futureEntries"
                      values={{ count: warning.count ?? 0 }}
                      message="{count, plural, one {There is # future schedule entry beyond today} other {There are # future schedule entries beyond today}}"
                    />
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        <BaseEditableForm<SignOffFormData, SignOffFormInput>
          schema={schema}
          fields={fields}
          groups={groups}
          defaultValues={defaultValues}
          mode={isSignedOff ? "edit" : "create"}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          onDelete={isSignedOff ? handleDelete : undefined}
          onFieldChange={handleFieldChange}
          isSubmitting={isSubmitting}
          isDeleting={undoSignOffMutation.isPending}
          inSheet
        />
      </DialogContent>
    </Dialog>
  );
}
